import React, { useState, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import studentAxios from "../constants/studentAxios";

const Homework = () => {
  const studentId = localStorage.getItem("studentId");
  const sessionId = localStorage.getItem("currentSessionId");

  const [student, setStudent] = useState(null);
  const [homeworkData, setHomeworkData] = useState([]);
  const [last30Days, setLast30Days] = useState([]);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const scrollContainerRef = useRef(null);

  // Generate last 30 days
  useEffect(() => {
    const today = new Date();
    const days = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      return d.toISOString().split("T")[0];
    });
    setLast30Days(days);
  }, []);

  // Fetch student and homework
  useEffect(() => {
    if (!studentId || !sessionId) return;

    const fetchData = async () => {
      try {
        const studentRes = await studentAxios.get(`/students/getStudents/${studentId}`);
        const studentData = studentRes.data;
        setStudent(studentData);

        if (!studentData.class || !studentData.section) {
          console.error("Class or Section not resolved");
          setHomeworkData([]);
          return;
        }

        const hwRes = await studentAxios.get(
          `/homework/getHomeworkByClassAndSectionName/${encodeURIComponent(studentData.class)}/${encodeURIComponent(studentData.section)}`,
          { params: { sessionId } }
        );

        setHomeworkData(hwRes.data);
      } catch (err) {
        console.error("Homework fetch error: ", err);
        setHomeworkData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentId, sessionId]);

  // Center date scroll
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const badge = container.children[selectedDateIndex];
    if (badge) {
      const offset = badge.offsetLeft - container.clientWidth / 2 + badge.clientWidth / 2;
      container.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [selectedDateIndex, last30Days]);

  const formatDate = (d) => {
    const [y, m, day] = d.split("-");
    return `${day}-${m}-${y}`;
  };

  const filteredHomework =
    homeworkData.find((h) => h.dateAssigned === last30Days[selectedDateIndex])?.homework || {};

  if (loading) {
    return <div className="flex justify-center items-center h-screen text-lg font-semibold">Loading…</div>;
  }

  return (
    <div className="container mx-auto p-4">
     
      {/* Date Scroller */}
      <div className="flex items-center gap-2 mb-6">
        <button
          className="p-2 rounded-full bg-blue-200 hover:bg-blue-300 transition"
          onClick={() => setSelectedDateIndex(i => Math.max(i - 1, 0))}
        >
          <FaChevronLeft />
        </button>

        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-none gap-3 w-full"
        >
          {last30Days.map((d, i) => (
            <span
              key={d}
              onClick={() => setSelectedDateIndex(i)}
              className={`cursor-pointer px-5 py-2 rounded-full font-semibold text-sm whitespace-nowrap transition ${
                i === selectedDateIndex
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-blue-100 text-blue-800 hover:bg-blue-200"
              }`}
            >
              {formatDate(d)}
            </span>
          ))}
        </div>

        <button
          className="p-2 rounded-full bg-blue-200 hover:bg-blue-300 transition"
          onClick={() => setSelectedDateIndex(i => Math.min(i + 1, last30Days.length - 1))}
        >
          <FaChevronRight />
        </button>
      </div>

      {/* Homework Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Object.keys(filteredHomework).length > 0 ? (
          Object.entries(filteredHomework).map(([subject, hw], i) => (
            <div
              key={subject}
              className="p-5 bg-white rounded-xl shadow-md hover:shadow-xl transition-transform transform hover:-translate-y-1"
            >
              <h3 className="text-lg font-semibold mb-2 text-gray-800">{subject}</h3>
              <p className="text-gray-700">{hw}</p>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 col-span-full mt-6 text-lg">No homework for this date</p>
        )}
      </div>
    </div>
  );
};

export default Homework;
